import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { ArrowLeft, Pencil, Archive, Plus, Building2, User, CalendarDays } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import type CrmFundraisingOpportunity from '@/types/serializers/CrmFundraisingOpportunity'
import type CrmFundraisingDonation from '@/types/serializers/CrmFundraisingDonation'
import type CrmFundraisingRecurringDonation from '@/types/serializers/CrmFundraisingRecurringDonation'
import {
  OPPORTUNITY_STATUS_LABELS,
  STATUS_BADGE_VARIANT,
} from '@/lib/fundraising-labels'
import { FinancialSummary } from './_components/FinancialSummary'
import { LinkedDonationsTable } from './_components/LinkedDonationsTable'
import { EditOpportunitySheet } from './_components/EditOpportunitySheet'
import { OpportunityContacts } from './_components/OpportunityContacts'
import { TasksSlot } from '@/components/work/TasksSlot'

interface OpportunityContactData {
  id: number
  contact_id: number
  contact_name: string
  contact_email?: string
  role_key: string
  role: { value: number; label: string }
}

interface Props {
  opportunity: CrmFundraisingOpportunity
  donations: CrmFundraisingDonation[]
  recurring_donations: CrmFundraisingRecurringDonation[]
  opportunity_contacts?: OpportunityContactData[]
}

const TRANSITION_MAP: Record<string, Array<{ event: string; label: string; variant?: 'default' | 'destructive' | 'outline' }>> = {
  prospection: [
    { event: 'qualify', label: 'Qualifier' },
    { event: 'lose', label: 'Perdre', variant: 'destructive' },
  ],
  qualification: [
    { event: 'propose', label: 'Proposer' },
    { event: 'lose', label: 'Perdre', variant: 'destructive' },
  ],
  proposal: [
    { event: 'negotiate', label: 'Negocier' },
    { event: 'lose', label: 'Perdre', variant: 'destructive' },
  ],
  negotiation: [
    { event: 'win', label: 'Gagner' },
    { event: 'lose', label: 'Perdre', variant: 'destructive' },
  ],
  won: [
    { event: 'close', label: 'Cloturer', variant: 'outline' },
  ],
}

export default function OpportunityShow({
  opportunity,
  donations,
  recurring_donations,
  opportunity_contacts = [],
}: Props) {
  const [editOpen, setEditOpen] = useState(false)

  const statusKey = (opportunity as Record<string, unknown>).status_key as string ?? (typeof opportunity.status === 'object' ? '' : String(opportunity.status))
  const statusLabel = typeof opportunity.status === 'object' ? opportunity.status.label : (OPPORTUNITY_STATUS_LABELS[statusKey] ?? statusKey)
  const transitions = TRANSITION_MAP[statusKey] ?? []
  const isTerminal = statusKey === 'lost' || statusKey === 'closed'
  const isOrgDonor = opportunity.donor_type?.includes('OrganizationDonor')

  function handleTransition(event: string) {
    router.post(`/fundraising/opportunities/${opportunity.id}/transition`, { event })
  }

  function handleArchive() {
    router.patch(`/fundraising/opportunities/${opportunity.id}/archive`)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <Link
            href="/fundraising/opportunities"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            Opportunites
          </Link>
          <h1 className="text-2xl font-bold tracking-tight">{opportunity.name}</h1>
          <div className="flex items-center gap-3 flex-wrap">
            <Badge variant={STATUS_BADGE_VARIANT[statusKey] ?? 'outline'}>
              {statusLabel}
            </Badge>
            <span className="text-sm text-muted-foreground tabular-nums">
              {opportunity.estimated_amount_formatted}
            </span>
            {opportunity.donor_name && (
              <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                {isOrgDonor ? <Building2 className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}
                {opportunity.donor_name}
              </span>
            )}
            {opportunity.assignee_name && (
              <span className="text-sm text-muted-foreground">
                Responsable : {opportunity.assignee_name}
              </span>
            )}
            {opportunity.expected_close_date && (
              <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                <CalendarDays className="h-3.5 w-3.5" />
                {new Date(opportunity.expected_close_date).toLocaleDateString('fr-FR')}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isTerminal && (
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Pencil className="mr-1.5 h-4 w-4" />
              Modifier
            </Button>
          )}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Archive className="mr-1.5 h-4 w-4" />
                Archiver
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Archiver cette opportunite ?</AlertDialogTitle>
                <AlertDialogDescription>
                  L'opportunite sera archivee et ne sera plus visible dans la liste par defaut.
                  Vous pourrez la restaurer a tout moment.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction onClick={handleArchive}>
                  Archiver
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Transition buttons */}
      {transitions.length > 0 && (
        <div className="flex items-center gap-2">
          {transitions.map(({ event, label, variant }) => (
            <Button
              key={event}
              variant={variant ?? 'default'}
              size="sm"
              onClick={() => handleTransition(event)}
            >
              {label}
            </Button>
          ))}
        </div>
      )}

      <Separator />

      {/* Description */}
      {opportunity.description && (
        <div className="text-sm text-muted-foreground whitespace-pre-wrap">
          {opportunity.description}
        </div>
      )}

      {/* Financial summary */}
      {opportunity.total_promised_formatted && opportunity.total_received_formatted && (
        <FinancialSummary
          totalPromised={opportunity.total_promised_formatted}
          totalReceived={opportunity.total_received_formatted}
          realizationRate={opportunity.realization_rate ?? 0}
          engagementGap={opportunity.engagement_gap_formatted}
        />
      )}

      {/* Contacts section (org donors only) */}
      {isOrgDonor && (
        <OpportunityContacts
          opportunityId={opportunity.id}
          contacts={opportunity_contacts}
          isTerminal={isTerminal}
        />
      )}

      {/* Tabs */}
      <Tabs defaultValue="donations">
        <TabsList>
          <TabsTrigger value="donations">
            Dons ({opportunity.donations_count ?? donations.length})
          </TabsTrigger>
          <TabsTrigger value="recurring">
            Dons recurrents ({opportunity.recurring_donations_count ?? recurring_donations.length})
          </TabsTrigger>
          <TabsTrigger value="taches">Tâches</TabsTrigger>
        </TabsList>

        <TabsContent value="donations" className="space-y-4">
          {!isTerminal && (
            <div className="flex justify-end">
              <Button asChild size="sm">
                <Link href={`/fundraising/donations?create=true&opportunity_id=${opportunity.id}`}>
                  <Plus className="mr-1.5 h-4 w-4" />
                  Ajouter un don
                </Link>
              </Button>
            </div>
          )}
          <LinkedDonationsTable donations={donations} type="donation" />
        </TabsContent>

        <TabsContent value="recurring">
          <LinkedDonationsTable
            donations={[]}
            type="recurring"
            recurringDonations={recurring_donations}
          />
        </TabsContent>

        <TabsContent value="taches" className="space-y-4">
          <TasksSlot
            subjectType="CrmFundraising::Opportunity"
            subjectId={opportunity.id}
            isSubjectClosed={isTerminal}
          />
        </TabsContent>
      </Tabs>

      <EditOpportunitySheet
        open={editOpen}
        onOpenChange={setEditOpen}
        opportunity={opportunity}
      />
    </div>
  )
}
